import { AccountCircleOutlined, ChevronRight, KeyboardArrowRight } from "@mui/icons-material";
import { Avatar, Box, Card, CardContent, CardHeaderProps, Checkbox, FormControlLabel, FormGroup, CardHeader as MuiCardHeader, Typography, buttonBaseClasses, linkClasses } from "@mui/material";
import moment from "moment";
import NavLink from "../../components/NavLink";
import AccentPaper from "../../components/Paper";
import { useIsMobile, useRemoteList, useTenant, useTenantId } from "../../models/Hooks";
import { fioOrName, getUser, hasHouses, hasKey, hasRoles } from "../../models/Identity";
import { getTenant, isSystemTenant } from "../../models/Tenant";
import { Meter } from "../../models/Types";
import { TopAppBar } from "../../nav/AppBar";
import TenantSelectorComponent from "../../nav/TenantSelector";
import { isMobile, moneyFormat, parseValidDate } from "../../utils";
import { BillWidget } from "../bill/BillWidget";
import { EntryPassWidget } from "../entrypass/EntryPassWidget";
import { MeasuresWidget } from "../measure/MeasuresWidget";
import { PaymentWidget } from "../payment/PaymentWidget";
import { PostWidget } from "../post/PostWidget";
import { ServiceWidget } from "../project/service/ServiceWidget";
import { RequestWidget } from "../request/RequestWidget";
import PostStories from "../post/PostStories";
import { ReactNode } from "react";
import Title from "../../icons/Title";
import { root } from "../../models/Proxy";
import FaviconAvatar from "../../icons/Favicon";

const styles = {
    desktop: {
        inhabitant: {
            '& > *:nth-child(1)': { gridArea: '1 / 1 / 2 / 3', minHeight: 380 },
            '& > *:nth-child(2)': { gridArea: '1 / 3 / 2 / 4', minHeight: 380 },
            '& > *:nth-child(3)': { gridArea: '2 / 1 / 3 / 2', minHeight: 300 },
            '& > *:nth-child(4)': { gridArea: '2 / 2 / 3 / 3', minHeight: 300 },
            '& > *:nth-child(5)': { gridArea: '2 / 3 / 3 / 4', minHeight: 300 },
            '& > *:nth-child(6)': { gridArea: '3 / 1 / 4 / 4', minHeight: 330 },
        },
        employee: {
            '& > *:nth-child(1)': { gridArea: '1 / 1 / 2 / 3', minHeight: 380 },
            '& > *:nth-child(2)': { gridArea: '1 / 3 / 3 / 4' },
            '& > *:nth-child(3)': { gridArea: '2 / 1 / 3 / 3', minHeight: 300 },
            '& > *:nth-child(4)': { gridArea: '3 / 1 / 4 / 4', minHeight: 330 }
        },
        system: {
            '& > *:nth-child(1)': { gridArea: '1 / 1 / 2 / 3', minHeight: 380 },
            '& > *:nth-child(2)': { gridArea: '1 / 3 / 3 / 4' },
            '& > *:nth-child(3)': { gridArea: '2 / 1 / 3 / 3', minHeight: 330 }
        },
        root: {
            py: 2, overflowY: 'auto',
            gap: 2, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(3, auto)',
            '& .MuiCard-root': {
                display: 'flex', flexDirection: 'column', p: 3.75, gap: 2,
                '& > div': {
                    p: 0
                },
                '& .MuiCardHeader-title': {
                    fontWeight: 700,
                    [`& > .${linkClasses.root}[data-route]:not(.${buttonBaseClasses.root})`]: {
                        textDecoration: 'none',
                        fontSize: '.5em',
                        fontWeight: 400,
                        marginLeft: '1em',
                        display: 'inline-flex',
                        alignItems: 'center',
                        '& > .MuiSvgIcon-root': {
                            fontSize: 'inherit'
                        }
                    },
                    [`& > .${buttonBaseClasses.root}[data-route]`]: {
                        p: .5,
                        minWidth: 0
                    }
                },
                '& .MuiTableCell-root:first-child, & .MuiTableCell-root:first-child > .MuiTypography-root': {
                    pl: 0,
                    justifyContent: 'left',
                    textAlign: 'left'
                },
                '& .MuiTableCell-root:last-child, & .MuiTableCell-root:last-child > .MuiTypography-root': {
                    pr: 0
                },
                '& .MuiTableCell-head': {
                    bgcolor: 'background.paper'
                },
                '& .MuiTableCell-root .routerlink-icon': {
                    display: 'none!important'
                }
            }
        }
    }
}

const widgets: { [key: string]: () => ReactNode } = {
    Request: () => <RequestWidget />,
    Bill: () => <BillWidget />,
    EntryPass: () => <EntryPassWidget />,
    Measure: () => <MeasuresWidget />,
    Meter: () => <MeterWidget />,
    Payment: () => <PaymentWidget />,
    Service: () => <ServiceWidget />
}

const roles = ['Request', 'Payment', 'EntryPass'];
const mobile = ['EntryPass', 'Request', 'Service'];

styles.desktop.inhabitant = { ...styles.desktop.root, ...styles.desktop.inhabitant };
styles.desktop.employee = { ...styles.desktop.root, ...styles.desktop.employee };
styles.desktop.system = { ...styles.desktop.root, ...styles.desktop.system };

function CardHeader(props: CardHeaderProps) {
    return <MuiCardHeader {...props}
        titleTypographyProps={{ variant: 'h1', ...props?.titleTypographyProps }}
        subheaderTypographyProps={{ variant: 'body2', ...props?.subheaderTypographyProps }}
    />;
}

function getWidth(w?: number) {
    if (isMobile()) {
        return 'auto';
    }

    w || (w = 1);
    // return w * 345 + (16 * (w - 1));

    return 'calc(' + (w * 100 / 3) + '% - ' + (16 * (w - 1)) + 'px)';
}

function MeterWidget() {
    const [data] = useRemoteList<Meter>('MeterList', d => {
        d.Data.forEach(x => x.NextCheck = parseValidDate(x.NextCheck));
        return d;
    });
    return <Card sx={{ flex: 1 }}>
        <CardHeader title="Счетчики" subheader="В случае просрочки поверки, показания счетчика не принимаются" />
        <CardContent sx={{ p: 0, "& td:last-child": { textAlign: 'right' } }} component="table" cellSpacing={16}>
            {data?.filter(x => x.NextCheck).map(x => <tr>
                <td><NavLink route={[['page', 'meter'], ['id', x.Id]]}>{x.Name ? (x.Name + ' (' + x.Number + ')') : x.Number}</NavLink></td>
                <td>{x.NextCheck ? <Typography color={x.NextCheck <= new Date() ? 'error' : 'textPrimary'}>{moment(x.NextCheck).calendar()}</Typography> : null}</td>
            </tr>)}
        </CardContent>
    </Card>;
}


function TasksWidget() {
    return <Card sx={{ width: getWidth() }}>
        <CardHeader title="Задачи" subheader={<>Активных: <b>1</b> Просрочено: <b>0</b></>} />
        <CardContent>
            <FormGroup>
                <FormControlLabel control={<Checkbox />} label={<NavLink route={[['guide', 'welcome']]}>Пройти ознакомительный тур</NavLink>} />
            </FormGroup>
        </CardContent>
    </Card>;
}

export default function Dashboard() {
    const ism = useIsMobile();
    useTenant();
    
    if (ism) {
        const hasHs = hasHouses();
        return <>
            <TopAppBar left={<NavLink color="textPrimary" route={[['page', 'profile']]} variant="h3" px={1} display="flex" alignItems="center" gap={.75}>                
                <FaviconAvatar />
                
                Здравствуйте, {getTenant()?.Person?.Name || getUser()?.Name || getUser()?.UserName}!
                {/*<ChevronRight fontSize="small" />*/}
            </NavLink>} />

            <Box sx={{ overflow: 'auto', p: 2 }}>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {hasHs ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TenantSelectorComponent />

                        <PostStories />

                        <AccentPaper sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h2">
                                <Typography variant="body1" component="span">Ваш баланс: </Typography>
                                {moneyFormat(getUser().Tenants.map(x => x.Houses?.map(x => x.Balance || 0).reduce((a, b) => a + b, 0)).filter(x => x).reduce((a, b) => a + b, 0))}
                            </Typography>
                            {/**<NavLink sx={{ textDecoration: 'none' }} href="#">
                                <Typography variant="caption" display="flex" alignItems="center">Пополнить <KeyboardArrowRight fontSize="inherit" /></Typography>
                            </NavLink>{/**/}
                        </AccentPaper>

                        <Box display="flex" gap={1} sx={{ "&>*": { flex: 1 } }}>
                            <BillWidget />

                            <MeasuresWidget />
                        </Box>
                    </Box> : null}

                    {mobile.filter(x => hasHs || hasKey(x)).map(x => widgets[x]())}

                    {hasHs ? <PostWidget /> : null}
                </Box>
            </Box>

        </>;
    } else if (hasRoles()) {
        return <Box sx={isSystemTenant() ? styles.desktop.system : styles.desktop.employee}>
            {roles.filter(x => hasKey(x)).map(x => widgets[x]())}
        </Box>;
    } else {
        return <Box sx={styles.desktop.inhabitant}>
            <RequestWidget />
            <BillWidget />

            <EntryPassWidget />
            <MeasuresWidget />
            <MeterWidget />

            <PostWidget />
        </Box>;
    }
}